import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
  Input,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { auditTime, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  O2OInternalModel,
  O2OInternalSearchParams,
} from './o2o-internal.model';
import { O2OInternalService } from './o2o-internal.service';
import { AfterSalesReason } from '@app/api/order-service/model/afterSalesReason';
import { O2oCancelResultVO } from '@app/api/order-service/model/o2oCancelResultVO';
import { DeliveryRetryFeatureService } from '@app/feature/configuration/order/delivery-retry-config/services/delivery-retry-config.service';
import { Router } from '@angular/router';
import {
  O2O_INTERNAL_CONFIG,
  O2O_INTERNAL_DEFAULT_COLUMNS_ORDER,
  O2O_INTERNAL_FLEXIBLE_COLUMNS_ORDER,
  O2O_INTERNAL_ACTION_COLUMN,
} from '../services/o2o.config';
import { Tag } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import { InternalOrderConfig } from '../../order/internal/internal.config';
import {
  o2oInternalGetStatusDisplayText,
  o2oInternalGetStatusSeverity,
} from './o2o-internal-table-status.util';
import { O2OOrderService } from '../services/o2o-order.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { O2OOrderViewComponent } from '../view/o2o-order-view.component';
import { OrderItemDialogComponent } from '../../order/internal/components/order-item-dialog/order-item-dialog.component';
import { PermissionService } from '@app/core/services/permission.service';
import { AuthService } from '@app/core/services/auth.service';
import {
  O2O_NEXT_ACTION,
  O2oNextActionCode,
  rowHasO2oNextAction,
} from './o2o-internal-next-actions.util';
import {
  OrderEvent,
  OrderSseService,
} from '@app/core/services/order-sse.service';
import {
  formatTimeoutMinutes,
  getStockHoldCategoryLabelKey,
  hasStockHoldExpiryDisplay,
  isStockHoldExpiryExpired,
  isStockHoldExpiringSoon,
  isStockHoldTimeoutOrder,
} from '@app/shared/utils/stock-hold-timeout.util';

@Component({
  selector: 'app-o2o-internal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    AppButtonComponent,
    Tag,
    TooltipModule,
    OrderItemDialogComponent,
    O2OOrderViewComponent,
    ToastModule,
    DialogModule,
    InputTextModule,
    SelectModule,
  ],
  providers: [MessageService],
  templateUrl: './o2o-internal.component.html',
  styleUrl: './o2o-internal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class O2OInternalComponent implements OnInit, OnDestroy {
  private readonly o2oInternalService = inject(O2OInternalService);
  private readonly deliveryRetryFeature = inject(DeliveryRetryFeatureService);
  private readonly o2oOrderService = inject(O2OOrderService);
  private readonly router = inject(Router);
  private readonly orderSseService = inject(OrderSseService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly destroy$ = new Subject<void>();
  private readonly sseRefresh$ = new Subject<void>();
  private readonly sseStatus$ = new Subject<void>();
  private readonly pendingSseStatusEvents = new Map<string, OrderEvent>();
  private readonly latestSseStatusByOrderNo = new Map<
    string,
    {
      orderStatus?: string | number;
      fulfillmentStatus?: string | number;
      eventTimeMs: number;
    }
  >();
  private readonly pendingSseToastOrderNos = new Set<string>();
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private sseConnected = false;

  @Input() businessType = 'O2O,STOREFRONT';
  @Input() titleKey = 'o2o.internal.title';
  @Input() createRoute: string | null = '/o2o/internal/add';
  @Input() viewRoute = '/o2o/view';
  @Input() permissionPrefix = 'o2o_orders.o2o_orders';
  @Input() enableFulfillmentActions = true;
  @Input() enableSse = true;

  orders = signal<O2OInternalModel[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  // Permission flags - computed from PermissionService
  // Using specific submenu 'o2o_orders' for O2O orders permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.view`);
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.create`);
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.edit`);
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.delete`);
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.approve`);
  });

  // Configuration from config file
  searchConfig = withPaginatedSearchFields(
    O2O_INTERNAL_CONFIG.searchConfig,
    {
      salesStore: {
        loadPage: (params) =>
          this.o2oOrderService.getSalesStoresPage({
            ...params,
            status: true,
          }),
      },
    },
  );
  statusOptions = O2O_INTERNAL_CONFIG.statusOptions;

  activeStatus = signal('all');

  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      (this.enableFulfillmentActions &&
        (this.canDelete() || this.canApprove() || this.canUpdate())),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      O2O_INTERNAL_CONFIG.tableColumns.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of O2O_INTERNAL_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(O2O_INTERNAL_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      O2O_INTERNAL_CONFIG.tableColumns.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of O2O_INTERNAL_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Search parameters (using 1-based pagination like brand component)
  searchParams = signal<{
    page: number;
    size: number;
    searchData?: SearchFormData;
  }>({
    page: 1, // Use 1-based pagination
    size: 20,
  });

  // Primary button configuration base
  private readonly primaryButtonBase = O2O_INTERNAL_CONFIG.primaryButton;

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate() && !!this.createRoute,
  }));

  // Order item dialog state
  showOrderItemDialog = signal(false);
  selectedOrderId = signal<number | null>(null);
  selectedOrderNumber = signal<string | null>(null);

  /** True while POST /v1/order/{orderId}/o2o/cancel is in flight. */
  cancelSubmitting = signal(false);

  /** Cancel dialog: order ids to cancel (numeric). */
  cancelPendingOrderIds = signal<number[]>([]);

  cancelDialogVisible = signal(false);

  /** Cancel reasons catalogue (type=100), filtered to the SELLER_* bucket for this BO surface. */
  cancelReasons = signal<AfterSalesReason[]>([]);
  cancelReasonCode = signal<string | null>(null);
  cancelReasonCodeShowError = signal(false);
  cancelNoteRequired = computed(() =>
    (this.cancelReasonCode() || '').endsWith('_OTHER'),
  );

  cancelReasonText = signal('');
  cancelReasonShowError = signal(false);

  /** Order id for cancel / audit dialog detail (same as O2O detail page). */
  dialogDetailOrderId = signal<number | null>(null);

  auditDialogVisible = signal(false);
  auditPendingOrderId = signal<number | null>(null);
  auditRejectReason = signal('');
  auditRejectReasonShowError = signal(false);
  auditSubmitting = signal(false);

  receiveDialogVisible = signal(false);
  receivePendingOrderId = signal<number | null>(null);
  receiveNotes = signal('');
  receiveSubmitting = signal(false);
  deliveryFailedDialogVisible = signal(false);
  deliveryFailedPendingOrderId = signal<number | null>(null);
  deliveryFailedNotes = signal('');
  deliveryFailedSubmitting = signal(false);

  readonly nextAction = O2O_NEXT_ACTION;

  ngOnInit(): void {
    this.loadOrders();
    if (this.enableSse) {
      this.setupSseSubscription();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sseRefresh$.complete();
    this.sseStatus$.complete();
    if (this.sseConnected) {
      this.orderSseService.disconnect();
    }
  }

  private setupSseSubscription(): void {
    this.orderSseService.connect();
    this.sseConnected = true;
    this.sseRefresh$
      .pipe(auditTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadOrders();
      });
    this.sseStatus$
      .pipe(auditTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.flushO2oStatusEvents();
      });

    this.orderSseService.orderEvents
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.eventType === 'o2o-status-updated') {
          if (event.innerOrderNo) {
            this.queueO2oStatusEvent(event);
          }
          this.sseRefresh$.next();
          return;
        }

        if (
          event.eventType === 'modification-pending-approval' ||
          event.eventType === 'modification-expired' ||
          event.eventType === 'modification-approved' ||
          event.eventType === 'modification-rejected'
        ) {
          this.handleModificationSseEvent(event);
          this.sseRefresh$.next();
        }
      });
  }

  private handleModificationSseEvent(event: OrderEvent): void {
    const orderNo = event.innerOrderNo || event.outerOrderNo || '-';
    const currentUsername = this.authService.currentUser()?.username || '';
    const requiredLevel = event.requiredApprovalLevel || 1;

    if (event.eventType === 'modification-pending-approval') {
      if (!this.canApproveModificationLevel(requiredLevel)) {
        return;
      }
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('common.messages.info'),
        detail: this.translateService.instant(
          'order_modification.messages.sse_pending',
          { orderNo, level: requiredLevel },
        ),
      });
      return;
    }

    const isRequester =
      !!event.requestedBy &&
      !!currentUsername &&
      event.requestedBy === currentUsername;
    if (!isRequester && event.audience === 'REQUESTER') {
      return;
    }

    const keyMap: Record<string, string> = {
      'modification-expired': 'order_modification.messages.sse_expired',
      'modification-approved': 'order_modification.messages.sse_approved',
      'modification-rejected': 'order_modification.messages.sse_rejected',
    };
    const key = keyMap[event.eventType || ''];
    if (!key) {
      return;
    }
    this.messageService.add({
      severity: event.eventType === 'modification-expired' ? 'warn' : 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(key, {
        orderNo,
        level: requiredLevel,
      }),
    });
  }

  private canApproveModificationLevel(requiredLevel: number): boolean {
    const prefix = this.permissionPrefix;
    if (
      requiredLevel <= 3 &&
      this.permissionService.canDoAction(`${prefix}.approve_mod_l3`)
    ) {
      return true;
    }
    if (
      requiredLevel <= 2 &&
      this.permissionService.canDoAction(`${prefix}.approve_mod_l2`)
    ) {
      return true;
    }
    if (
      requiredLevel <= 1 &&
      this.permissionService.canDoAction(`${prefix}.approve_mod_l1`)
    ) {
      return true;
    }
    return false;
  }

  private queueO2oStatusEvent(event: OrderEvent): void {
    if (
      !event.innerOrderNo ||
      (event.orderStatus == null && event.fulfillmentStatus == null)
    ) {
      return;
    }

    const eventTimeMs = this.toEventTimeMs(event);
    const pendingEvent = this.pendingSseStatusEvents.get(event.innerOrderNo);
    const latestStatus = this.latestSseStatusByOrderNo.get(event.innerOrderNo);
    const newestKnownTime = Math.max(
      pendingEvent
        ? this.toEventTimeMs(pendingEvent)
        : Number.NEGATIVE_INFINITY,
      latestStatus?.eventTimeMs ?? Number.NEGATIVE_INFINITY,
    );

    if (eventTimeMs < newestKnownTime) {
      return;
    }

    this.pendingSseStatusEvents.set(event.innerOrderNo, event);
    this.sseStatus$.next();
  }

  private flushO2oStatusEvents(): void {
    const events = [...this.pendingSseStatusEvents.values()];
    this.pendingSseStatusEvents.clear();

    let latestUpdatedEvent: OrderEvent | null = null;
    for (const event of events) {
      const updated = this.applyO2oStatusEvent(event);
      if (updated) {
        latestUpdatedEvent = event;
        this.pendingSseToastOrderNos.add(
          event.innerOrderNo || event.outerOrderNo || '-',
        );
      }
    }

    if (latestUpdatedEvent) {
      this.showO2oStatusUpdatedToast(latestUpdatedEvent);
    }
  }

  private applyO2oStatusEvent(event: OrderEvent): boolean {
    if (
      !event.innerOrderNo ||
      (event.orderStatus == null && event.fulfillmentStatus == null)
    ) {
      return false;
    }

    const eventTimeMs = this.toEventTimeMs(event);
    const latestStatus = this.latestSseStatusByOrderNo.get(event.innerOrderNo);
    if (latestStatus && eventTimeMs < latestStatus.eventTimeMs) {
      return false;
    }

    this.latestSseStatusByOrderNo.set(event.innerOrderNo, {
      orderStatus: event.orderStatus,
      fulfillmentStatus: event.fulfillmentStatus,
      eventTimeMs,
    });

    let updated = false;
    this.orders.update((orders) =>
      orders.map((row) => {
        if (row.internalOrderNumber !== event.innerOrderNo) {
          return row;
        }
        updated = true;
        return {
          ...row,
          orderStatus: this.toStatusString(event.orderStatus, row.orderStatus),
          fulfillmentStatus: this.toStatusString(
            event.fulfillmentStatus,
            row.fulfillmentStatus,
          ),
        };
      }),
    );

    if (updated) {
      this.cdr.markForCheck();
    }
    return updated;
  }

  private applyLatestSseStatusOverrides(
    orders: O2OInternalModel[],
  ): O2OInternalModel[] {
    return orders.map((row) => {
      const orderNo = row.internalOrderNumber;
      if (!orderNo) {
        return row;
      }

      const latestStatus = this.latestSseStatusByOrderNo.get(orderNo);
      if (!latestStatus) {
        return row;
      }

      const orderStatus = this.toStatusString(
        latestStatus.orderStatus,
        row.orderStatus,
      );
      const fulfillmentStatus = this.toStatusString(
        latestStatus.fulfillmentStatus,
        row.fulfillmentStatus,
      );
      if (
        orderStatus === row.orderStatus &&
        fulfillmentStatus === row.fulfillmentStatus
      ) {
        // Reload caught up with the optimistic patch — drop it.
        this.latestSseStatusByOrderNo.delete(orderNo);
        return row;
      }

      return {
        ...row,
        orderStatus,
        fulfillmentStatus,
      };
    });
  }

  private toEventTimeMs(event: OrderEvent): number {
    const parsed = event.eventTime ? Date.parse(event.eventTime) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  }

  private showO2oStatusUpdatedToast(event: OrderEvent): void {
    const updatedOrderCount = this.pendingSseToastOrderNos.size;
    this.pendingSseToastOrderNos.clear();

    if (updatedOrderCount > 1) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant(
          'o2o.internal.messages.status_updated_summary',
        ),
        detail: this.translateService.instant(
          'o2o.internal.messages.status_updates_detail',
          { count: updatedOrderCount },
        ),
      });
      return;
    }

    const orderNo = event.innerOrderNo || event.outerOrderNo || '-';
    const statusKey = o2oInternalGetStatusDisplayText(
      'orderStatus',
      event.orderStatus,
    );
    const status = this.translateService.instant(statusKey);
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant(
        'o2o.internal.messages.status_updated_summary',
      ),
      detail: this.translateService.instant(
        'o2o.internal.messages.status_updated_detail',
        { order: orderNo, status },
      ),
    });
  }

  private toStatusString(
    value: string | number | undefined,
    fallback: string,
  ): string {
    if (value == null || value === '') {
      return fallback;
    }
    return String(value);
  }

  loadOrders(): void {
    this.loading.set(true);
    const currentSearchData = this.searchParams().searchData;
    const searchParams: O2OInternalSearchParams = {
      first: (this.searchParams().page - 1) * this.searchParams().size, // Convert 1-based to 0-based
      rows: this.searchParams().size,
      sortBy: 'creationDate',
      ascending: false,
      businessType: this.businessType,
      ...(currentSearchData && {
        ...(currentSearchData['orderNumber'] && {
          orderNumber: currentSearchData['orderNumber'],
        }),
        ...(currentSearchData['buyerNickname'] && {
          buyerNickname: currentSearchData['buyerNickname'],
        }),
        ...(currentSearchData['receiverName'] && {
          receiverName: currentSearchData['receiverName'],
        }),
        ...(currentSearchData['salesStore'] && {
          salesStore: currentSearchData['salesStore'],
        }),
        ...(currentSearchData['receiptStatus'] && {
          receiptStatus: currentSearchData['receiptStatus'],
        }),
        ...(currentSearchData['isGift'] && {
          isGift: currentSearchData['isGift'],
        }),
        ...(currentSearchData['mobile'] && {
          mobile: currentSearchData['mobile'],
        }),
        ...(currentSearchData['stockHoldTimeoutOnly'] === 'yes' && {
          stockHoldTimeoutOnly: 'yes' as const,
        }),
      }),
    };

    this.o2oInternalService.getOrders(searchParams).subscribe({
      next: (response) => {
        this.orders.set(this.applyLatestSseStatusOverrides(response.data));
        this.totalRecords.set(response.total);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Failed to load orders:', error);
        this.loading.set(false);
      },
    });
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({
      ...this.searchParams(),
      searchData,
      page: 1,
    });
    this.loadOrders();
  }

  onReset(): void {
    this.activeStatus.set('all');
    this.searchParams.set({
      page: 1,
      size: 20,
    });
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    this.activeStatus.set(status);
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadOrders();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1, // Advanced table uses 0-based, convert to 1-based
      size: event.rows,
    });
    this.loadOrders();
  }

  createOrder(): void {
    if (!this.canCreate()) {
      // Show warning message if needed
      return;
    }
    if (this.createRoute) {
      this.router.navigate([this.createRoute]);
    }
  }

  onView(rowData: any): void {
    if (!this.canRead()) {
      // Show warning message if needed
      return;
    }
    // Try different possible ID fields
    const orderId = rowData.id;

    if (orderId) {
      // Navigate to order view page
      this.router.navigate([this.viewRoute, orderId]);
    } else {
      console.error('Order ID not found in row data:', rowData);
    }
  }

  hasNextAction(row: O2OInternalModel, action: O2oNextActionCode): boolean {
    return rowHasO2oNextAction(row, action);
  }

  /** Fulfillment machine not started yet (e.g. hand-order waiting on workflow). */
  isFulfillmentNotStarted(row: O2OInternalModel): boolean {
    return String(row?.fulfillmentStatus ?? '') === '0';
  }

  onCancelOrderRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canDelete() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.CANCEL_ORDER)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'o2o.internal.cancel_order.missing_order_id',
        ),
      });
      return;
    }
    this.openCancelOrderDialog([id]);
  }

  cancelDialogTitle(): string {
    const ids = this.cancelPendingOrderIds();
    if (ids.length <= 1) {
      return this.translateService.instant(
        'o2o.internal.cancel_order.dialog_title_single',
      );
    }
    return this.translateService.instant(
      'o2o.internal.cancel_order.dialog_title_batch',
    );
  }

  cancelDialogHint(): string {
    const ids = this.cancelPendingOrderIds();
    if (ids.length <= 1) {
      const row = this.orders().find((o) => this.parseOrderId(o) === ids[0]);
      const label =
        row?.internalOrderNumber?.trim() ||
        row?.thirdPartyOrderNumber ||
        ids[0]?.toString() ||
        '';
      return this.translateService.instant(
        'o2o.internal.cancel_order.dialog_hint_single',
        { order: label },
      );
    }
    return this.translateService.instant(
      'o2o.internal.cancel_order.dialog_hint_batch',
      { count: ids.length },
    );
  }

  onCancelDialogVisibleChange(visible: boolean): void {
    this.cancelDialogVisible.set(visible);
    if (!visible) {
      this.cancelPendingOrderIds.set([]);
      this.cancelReasonCode.set(null);
      this.cancelReasonCodeShowError.set(false);
      this.cancelReasonText.set('');
      this.cancelReasonShowError.set(false);
      this.dialogDetailOrderId.set(null);
    }
  }

  onCancelReasonCodeChange(value: string | null): void {
    this.cancelReasonCode.set(value);
    if (value) {
      this.cancelReasonCodeShowError.set(false);
    }
    if (!(value || '').endsWith('_OTHER')) {
      this.cancelReasonShowError.set(false);
    }
  }

  onCancelReasonInput(value: string): void {
    this.cancelReasonText.set(value);
    if (value.trim()) {
      this.cancelReasonShowError.set(false);
    }
  }

  onAuditRejectReasonInput(value: string): void {
    this.auditRejectReason.set(value);
    if (value.trim()) {
      this.auditRejectReasonShowError.set(false);
    }
  }

  onConfirmCancelFromDialog(): void {
    const reasonCode = this.cancelReasonCode();
    if (!reasonCode) {
      this.cancelReasonCodeShowError.set(true);
      this.cdr.markForCheck();
      return;
    }
    const note = this.cancelReasonText().trim();
    if (this.cancelNoteRequired() && !note) {
      this.cancelReasonShowError.set(true);
      this.cdr.markForCheck();
      return;
    }
    const orderIds = this.cancelPendingOrderIds();
    const orderId = orderIds[0];
    if (orderId == null) {
      this.onCancelDialogVisibleChange(false);
      return;
    }

    this.cancelSubmitting.set(true);
    this.o2oInternalService
      .cancelO2oOrder(orderId, {
        cancelReasonCode: reasonCode,
        reason: note || undefined,
      })
      .subscribe({
        next: (result: O2oCancelResultVO) => {
          this.cancelSubmitting.set(false);
          const escalated =
            result?.result ===
            O2oCancelResultVO.ResultEnum.EscalatedManualIntervention;
          this.messageService.add({
            severity: escalated ? 'warn' : 'success',
            summary: this.translateService.instant(
              escalated ? 'common.messages.warning' : 'common.messages.success',
            ),
            detail: this.translateService.instant(
              escalated
                ? 'o2o.internal.cancel_order.escalated_after_handoff'
                : 'o2o.internal.cancel_order.success',
              { count: 1 },
            ),
          });
          this.onCancelDialogVisibleChange(false);
          this.loadOrders();
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.cancelSubmitting.set(false);
          const httpErr = err as HttpErrorResponse;
          const detail =
            (httpErr?.error &&
              (httpErr.error as { message?: string }).message) ||
            httpErr?.message ||
            this.translateService.instant('o2o.internal.cancel_order.error');
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail,
          });
          this.cdr.markForCheck();
        },
      });
  }

  onAuditOrderRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canApprove() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.AUDIT_ORDER)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.showMissingOrderIdWarning();
      return;
    }
    this.auditPendingOrderId.set(id);
    this.auditRejectReason.set('');
    this.auditRejectReasonShowError.set(false);
    this.dialogDetailOrderId.set(id);
    this.auditDialogVisible.set(true);
  }

  onAuditDialogVisibleChange(visible: boolean): void {
    this.auditDialogVisible.set(visible);
    if (!visible) {
      this.auditPendingOrderId.set(null);
      this.auditRejectReason.set('');
      this.auditRejectReasonShowError.set(false);
      this.dialogDetailOrderId.set(null);
    }
  }

  onApproveAudit(): void {
    this.submitAudit(true);
  }

  onRejectAudit(): void {
    const reason = this.auditRejectReason().trim();
    if (!reason) {
      this.auditRejectReasonShowError.set(true);
      this.cdr.markForCheck();
      return;
    }
    this.submitAudit(false, reason);
  }

  onConfirmCustomerReceiveRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canUpdate() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.CONFIRM_CUSTOMER_RECEIVE)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.showMissingOrderIdWarning();
      return;
    }
    this.receivePendingOrderId.set(id);
    this.receiveNotes.set('');
    this.receiveDialogVisible.set(true);
  }

  onConfirmDeliveryFailedRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canUpdate() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.CONFIRM_DELIVERY_FAILED)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.showMissingOrderIdWarning();
      return;
    }
    this.deliveryFailedPendingOrderId.set(id);
    this.deliveryFailedNotes.set('');
    this.deliveryFailedDialogVisible.set(true);
  }

  onRetryDeliveryRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canUpdate() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.RETRY_DELIVERY)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.showMissingOrderIdWarning();
      return;
    }
    this.deliveryRetryFeature.retryDelivery(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant(
            'o2o.internal.retry_delivery.success',
          ),
        });
        this.loadOrders();
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail:
            err?.error?.message ||
            this.translateService.instant('o2o.internal.retry_delivery.failed'),
        });
      },
    });
  }

  onReturnStockRow(row: O2OInternalModel): void {
    if (
      this.isFulfillmentNotStarted(row) ||
      !this.canUpdate() ||
      !this.hasNextAction(row, O2O_NEXT_ACTION.RETURN_STOCK)
    ) {
      return;
    }
    const id = this.parseOrderId(row);
    if (id == null) {
      this.showMissingOrderIdWarning();
      return;
    }
    this.deliveryRetryFeature.returnStock(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant(
            'o2o.internal.return_stock.success',
          ),
        });
        this.loadOrders();
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail:
            err?.error?.message ||
            this.translateService.instant('o2o.internal.return_stock.failed'),
        });
      },
    });
  }

  onReceiveDialogVisibleChange(visible: boolean): void {
    this.receiveDialogVisible.set(visible);
    if (!visible) {
      this.receivePendingOrderId.set(null);
      this.receiveNotes.set('');
    }
  }

  onDeliveryFailedDialogVisibleChange(visible: boolean): void {
    this.deliveryFailedDialogVisible.set(visible);
    if (!visible) {
      this.deliveryFailedPendingOrderId.set(null);
      this.deliveryFailedNotes.set('');
    }
  }

  onConfirmCustomerReceiveFromDialog(): void {
    const orderId = this.receivePendingOrderId();
    if (orderId == null) {
      this.onReceiveDialogVisibleChange(false);
      return;
    }
    const notes = this.receiveNotes().trim();
    this.receiveSubmitting.set(true);
    this.o2oInternalService
      .confirmCustomerReceive(orderId, notes ? { notes } : {})
      .subscribe({
        next: () => {
          this.receiveSubmitting.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'o2o.internal.confirm_customer_receive.success',
            ),
          });
          this.onReceiveDialogVisibleChange(false);
          this.loadOrders();
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.receiveSubmitting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.extractErrorMessage(
              err,
              'o2o.internal.confirm_customer_receive.error',
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  onConfirmDeliveryFailedFromDialog(): void {
    const orderId = this.deliveryFailedPendingOrderId();
    if (orderId == null) {
      this.onDeliveryFailedDialogVisibleChange(false);
      return;
    }
    const notes = this.deliveryFailedNotes().trim();
    this.deliveryFailedSubmitting.set(true);
    this.deliveryRetryFeature
      .markDeliveryFailed(orderId, notes || undefined)
      .subscribe({
        next: () => {
          this.deliveryFailedSubmitting.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'o2o.internal.confirm_delivery_failed.success',
            ),
          });
          this.onDeliveryFailedDialogVisibleChange(false);
          this.loadOrders();
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.deliveryFailedSubmitting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.extractErrorMessage(
              err,
              'o2o.internal.confirm_delivery_failed.error',
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private submitAudit(approved: boolean, auditReason?: string): void {
    const orderId = this.auditPendingOrderId();
    if (orderId == null) {
      return;
    }
    this.auditSubmitting.set(true);
    this.o2oInternalService
      .auditO2oOrder(orderId, {
        auditApproved: approved,
        auditReason: approved ? undefined : auditReason,
      })
      .subscribe({
        next: () => {
          this.auditSubmitting.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              approved
                ? 'o2o.internal.audit_order.approve_success'
                : 'o2o.internal.audit_order.reject_success',
            ),
          });
          this.onAuditDialogVisibleChange(false);
          this.loadOrders();
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.auditSubmitting.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.extractErrorMessage(
              err,
              'o2o.internal.audit_order.error',
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private showMissingOrderIdWarning(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: this.translateService.instant(
        'o2o.internal.cancel_order.missing_order_id',
      ),
    });
  }

  private extractErrorMessage(err: unknown, fallbackKey: string): string {
    const httpErr = err as HttpErrorResponse;
    return (
      (httpErr?.error && (httpErr.error as { message?: string }).message) ||
      httpErr?.message ||
      this.translateService.instant(fallbackKey)
    );
  }

  private openCancelOrderDialog(orderIds: number[]): void {
    const unique = [...new Set(orderIds)];
    this.cancelPendingOrderIds.set(unique);
    this.cancelReasonCode.set(null);
    this.cancelReasonCodeShowError.set(false);
    this.cancelReasonText.set('');
    this.cancelReasonShowError.set(false);
    const firstId = unique[0] ?? null;
    this.dialogDetailOrderId.set(firstId);
    this.cancelDialogVisible.set(true);
    this.loadCancelReasons();
  }

  /** Loads the SELLER_* cancel-reason bucket for this BO surface (AC-05). */
  private loadCancelReasons(): void {
    if (this.cancelReasons().length > 0) {
      return;
    }
    this.o2oInternalService.listCancelReasons().subscribe({
      next: (reasons) => {
        // Defensive: a misbehaving backend/gateway can return a non-array 2xx body.
        this.cancelReasons.set(
          Array.isArray(reasons)
            ? reasons.filter((r) => r.reasonCode?.startsWith('SELLER_'))
            : [],
        );
        this.cdr.markForCheck();
      },
      error: () => {
        this.cancelReasons.set([]);
        this.cdr.markForCheck();
      },
    });
  }

  private parseOrderId(row: O2OInternalModel): number | null {
    const n = Number(row.id);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
    return null;
  }

  onOrderItemDialogVisibleChange(visible: boolean): void {
    this.showOrderItemDialog.set(visible);
    if (!visible) {
      this.selectedOrderId.set(null);
      this.selectedOrderNumber.set(null);
    }
  }
  getOrderStatusLabel = InternalOrderConfig.getOrderStatusLabel;
  getPaymentStatusLabel = InternalOrderConfig.getPaymentStatusLabel;

  // Use shared status methods from InternalOrderConfig
  getPickupMethodLabel = InternalOrderConfig.getPickupMethodLabel;
  getPaymentMethodLabel = InternalOrderConfig.getPaymentMethodLabel;

  /** Delivery / receiving labels & severity: O2O list only; other columns use internal order config. */
  getStatusDisplayText(columnProp: string, value: unknown): string {
    return o2oInternalGetStatusDisplayText(columnProp, value);
  }

  getStatusSeverity(columnProp: string, value: unknown): string {
    return o2oInternalGetStatusSeverity(columnProp, value);
  }

  getStatusStyle(columnProp: string, value: unknown): Record<string, string> {
    const severity = this.getStatusSeverity(columnProp, value);
    const colorMap: Record<string, string> = {
      success: '#22c55e',
      danger: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      secondary: '#6b7280',
    };
    return {
      '--status-color': colorMap[severity] || '#6b7280',
    };
  }

  getStatusTooltip(columnProp: string, value: unknown): string {
    return this.getStatusDisplayText(columnProp, value);
  }

  getStockHoldCategoryLabel(category?: string): string {
    if (!category) {
      return this.translateService.instant(
        'stock_hold_timeout.order.no_limit',
      );
    }
    return this.translateService.instant(getStockHoldCategoryLabelKey(category));
  }

  formatStockHoldTimeout(minutes?: number): string {
    return formatTimeoutMinutes(minutes, this.translateService);
  }

  hasStockHoldExpiryDisplay = hasStockHoldExpiryDisplay;

  getStockHoldExpirySeverity(
    expiryTime?: string,
    failureReasonDescription?: string,
  ): string {
    if (isStockHoldExpiryExpired(expiryTime, failureReasonDescription)) {
      return 'danger';
    }
    if (isStockHoldExpiringSoon(expiryTime)) {
      return 'warn';
    }
    return 'info';
  }

  getStockHoldExpiryLabel(
    expiryTime?: string,
    failureReasonDescription?: string,
  ): string {
    if (isStockHoldExpiryExpired(expiryTime, failureReasonDescription)) {
      return this.translateService.instant('stock_hold_timeout.order.expired');
    }
    if (!expiryTime) {
      return this.translateService.instant('stock_hold_timeout.order.no_limit');
    }
    if (isStockHoldExpiringSoon(expiryTime)) {
      return this.translateService.instant(
        'stock_hold_timeout.order.expiring_soon',
      );
    }
    return this.formatDateTime(expiryTime);
  }

  getFailureReasonLabel(reason?: string): string {
    if (isStockHoldTimeoutOrder(reason)) {
      return this.translateService.instant(
        'stock_hold_timeout.order.failure.stock_hold_timeout',
      );
    }
    return reason || '';
  }

  getCancelReasonLabel(code: string): string {
    const key = `o2o.change_history.cancel_reasons.codes.${code}`;
    const label = this.translateService.instant(key);
    return label === key ? code : label;
  }

  formatDateTime = formatDateTime;
  formatCurrency = formatCurrencyVND;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
